/**
 * ConnectedChannels - Settings section for managing channel links
 * 
 * Features:
 * - List connected channels (Telegram, Slack, Discord)
 * - Generate linking codes
 * - Disconnect channels
 */
import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Link2, 
  Unlink, 
  Copy, 
  Check,
  Loader2,
  Clock,
  ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel icons (using simple emoji/text for now - could be replaced with SVGs)
const CHANNEL_ICONS = {
  telegram: '✈️',
  slack: '💬',
  discord: '🎮'
};

const CHANNEL_COLORS = {
  telegram: 'bg-blue-500',
  slack: 'bg-purple-500',
  discord: 'bg-indigo-500'
};

const ConnectedChannels = () => {
  const [links, setLinks] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingCode, setGeneratingCode] = useState(null);
  const [linkingCode, setLinkingCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [unlinking, setUnlinking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadChannelData();
  }, []);

  const loadChannelData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Fetch linked channels
      const linksRes = await fetch(`${API_URL}/api/channels/links`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (linksRes.ok) {
        const data = await linksRes.json();
        setLinks(data.links || []);
      }
      
      // Fetch available channels
      const availRes = await fetch(`${API_URL}/api/channels/available`);
      if (availRes.ok) {
        const data = await availRes.json();
        setAvailableChannels(data.channels || []);
      }
    } catch (err) {
      console.error('Error loading channels:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateLinkingCode = async (channel) => {
    setGeneratingCode(channel);
    setError('');
    setLinkingCode(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/channels/generate-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ channel })
      });
      
      if (res.ok) {
        const data = await res.json();
        setLinkingCode(data);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to generate code');
      }
    } catch (err) {
      setError('Failed to generate linking code');
    } finally {
      setGeneratingCode(null);
    }
  };

  const copyCode = async () => {
    if (linkingCode?.code) {
      await navigator.clipboard.writeText(linkingCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const unlinkChannel = async (channel) => {
    if (!confirm(`Disconnect ${channel}? You'll need to re-link to use it again.`)) {
      return;
    }
    
    setUnlinking(channel);
    
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/channels/${channel}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        loadChannelData();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to disconnect');
      }
    } catch (err) {
      setError('Failed to disconnect channel');
    } finally {
      setUnlinking(null);
    }
  };

  const isLinked = (channelId) => links.some(l => l.channel === channelId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-white">Connected Channels</h3>
          <p className="text-zinc-500 text-sm">Chat with your Companion from other apps</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Linking Code Modal */}
      {linkingCode && (
        <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-violet-400 text-sm font-medium">Your Linking Code</span>
            <button
              onClick={() => setLinkingCode(null)}
              className="text-zinc-500 hover:text-white text-sm"
            >
              Dismiss
            </button>
          </div>
          
          <div className="flex items-center gap-3 mb-3">
            <code className="flex-1 text-2xl font-mono text-white bg-black/30 px-4 py-3 rounded-lg text-center tracking-widest">
              {linkingCode.code}
            </code>
            <Button
              onClick={copyCode}
              variant="outline"
              size="icon"
              className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          
          <p className="text-zinc-400 text-sm mb-2">{linkingCode.instructions}</p>
          
          <div className="flex items-center gap-2 text-zinc-500 text-xs">
            <Clock className="w-3 h-3" />
            <span>Expires in {linkingCode.expires_in_minutes} minutes</span>
          </div>
        </div>
      )}

      {/* Connected Channels */}
      {links.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-zinc-400">Connected</h4>
          {links.map((link) => (
            <div 
              key={`${link.channel}-${link.channel_username}`}
              className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 ${CHANNEL_COLORS[link.channel] || 'bg-zinc-600'} rounded-lg flex items-center justify-center text-white`}>
                  {CHANNEL_ICONS[link.channel] || '💬'}
                </div>
                <div>
                  <p className="text-white font-medium capitalize">{link.channel}</p>
                  {link.channel_username && (
                    <p className="text-zinc-500 text-xs">@{link.channel_username}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {link.message_count > 0 && (
                  <span className="text-zinc-500 text-xs">{link.message_count} messages</span>
                )}
                <Button
                  onClick={() => unlinkChannel(link.channel)}
                  variant="ghost"
                  size="sm"
                  disabled={unlinking === link.channel}
                  className="text-zinc-400 hover:text-red-400"
                >
                  {unlinking === link.channel ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available Channels */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-zinc-400">
          {links.length > 0 ? 'Add More Channels' : 'Available Channels'}
        </h4>
        
        {availableChannels.map((channel) => {
          const linked = isLinked(channel.id);
          const isComingSoon = channel.status === 'coming_soon';
          
          return (
            <div 
              key={channel.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                linked 
                  ? 'bg-green-500/5 border-green-500/20' 
                  : 'bg-zinc-800/30 border-zinc-700/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 ${CHANNEL_COLORS[channel.id] || 'bg-zinc-600'} rounded-lg flex items-center justify-center text-white ${isComingSoon ? 'opacity-50' : ''}`}>
                  {CHANNEL_ICONS[channel.id] || '💬'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className={`font-medium ${isComingSoon ? 'text-zinc-500' : 'text-white'}`}>
                      {channel.name}
                    </p>
                    {isComingSoon && (
                      <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-0.5 rounded">
                        Coming Soon
                      </span>
                    )}
                    {linked && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                        Connected
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-xs">{channel.description}</p>
                </div>
              </div>
              
              {!linked && !isComingSoon && (
                <div className="flex items-center gap-2">
                  {channel.bot_username && (
                    <a
                      href={`https://t.me/${channel.bot_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-500 hover:text-zinc-300 text-xs flex items-center gap-1"
                    >
                      @{channel.bot_username}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <Button
                    onClick={() => generateLinkingCode(channel.id)}
                    disabled={generatingCode === channel.id}
                    variant="outline"
                    size="sm"
                    className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                  >
                    {generatingCode === channel.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Link2 className="w-4 h-4 mr-2" />
                        Connect
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help text */}
      <p className="text-zinc-600 text-xs">
        Connect your favorite messaging app to chat with your Companion on the go. 
        Your reflections sync automatically across all channels.
      </p>
    </div>
  );
};

export default ConnectedChannels;
